// packages/charts/src/drawnRows.ts
//
// Which rows a chart actually draws, in a leaf module: the option builder
// (`optionBuilder.ts`) draws from these rows and the series colours
// (`seriesColors.ts`) list the series of these rows, so the Format pane can
// never offer a swatch for a row the chart trimmed or refused, and an undrawn
// value never takes a by-value slot from a drawn one (ADR-0476; step 6 review,
// minor 1). Neither of the two may import the other, hence this file.

import { measuredNumber } from "./scatterSeries";
import { readTimeAxis } from "./timeAxis";

const toStr = (v: any) => (v === null || v === undefined ? "" : String(v));

/** Most index values look like ISO dates (`YYYY-MM-DD…`). */
export function shouldFormatAsDates(values: string[]): boolean {
  if (values.length === 0) return false;
  // Check if most values look like dates (YYYY-MM-DD)
  const datePattern = /^\d{4}-\d{2}-\d{2}/;
  const dateCount = values.filter(v => datePattern.test(v)).length;
  return dateCount / values.length > 0.5;
}

/** What `sortAndTrimDateRows` reads and rewrites of a spec. */
type RowsSpec = {
  rows?: Record<string, any>[] | null;
  y_keys?: string[] | null;
  comparison_rows?: unknown;
  target_rows?: unknown;
};

/**
 * A date index's rows in chronological order, with the trailing periods that
 * carry no value stripped (and the overlays cut where the rows are cut).
 * Anything else comes back as it went in.
 */
export function sortAndTrimDateRows<S extends RowsSpec>(spec: S, xKey: string): { spec: S; formatDates: boolean } {
  const axis = readTimeAxis(spec as unknown as Record<string, unknown>);
  // Safeguard: sort rows chronologically when x-axis contains dates
  const rawRows = spec.rows ?? [];
  const xRawUnsorted = rawRows.map((r) => toStr(r[xKey]));
  const formatDates = shouldFormatAsDates(xRawUnsorted);
  if (formatDates) {
    spec = { ...spec, rows: [...rawRows].sort((a, b) => toStr(a[xKey]).localeCompare(toStr(b[xKey]))) };
    // Strip trailing rows where all y-values are null/undefined (future
    // periods) — a chat spec's habit (6a1de53a6). Not on a time axis: there
    // the trailing nulls are the point.
    const yKeysForTrim = spec.y_keys ?? [];
    const rowIsEmpty = (row: Record<string, any>) =>
      yKeysForTrim.every((k) => {
        const v = row[k];
        return v === null || v === undefined;
      });
    // A series with NO value anywhere has no TAIL to strip: it is empty all
    // the way through, and the loop below would walk it down to its floor of
    // one row and make one arbitrary bucket the entire axis. Measured on a
    // report 2026-09-16 (Arjen): a month of `net_revenue` that dev had no data
    // for became a single "1 Aug" category, with all 31 values of the plan
    // overlay stacked on it. An empty month beside its plan line is a true
    // picture; one day carrying the whole month is not.
    const allRowsEmpty = (spec.rows ?? []).every(rowIsEmpty);
    let end = spec.rows!.length;
    while (!axis && !allRowsEmpty && end > 1) {
      if (rowIsEmpty(spec.rows![end - 1])) end--;
      else break;
    }
    if (end < spec.rows!.length) {
      // The overlays are built by INDEX from their own row lists further down
      // (`comparisonSeriesRows.map`, `targetSeriesRows.map`), and the axis is
      // built from `rows`. Trimming one without the other slides every
      // comparison value onto a neighbouring bucket — silently, because the
      // chart still draws. They are aligned to `rows` by contract (ADR-0267),
      // so they are cut where `rows` is cut. Array form only: a table's
      // comparison is a keyed lookup, which has no positional pairing to keep.
      spec = {
        ...spec,
        rows: spec.rows!.slice(0, end),
        ...(Array.isArray(spec.comparison_rows)
          ? { comparison_rows: spec.comparison_rows.slice(0, end) }
          : {}),
        ...(Array.isArray(spec.target_rows)
          ? { target_rows: spec.target_rows.slice(0, end) }
          : {}),
      };
    }
  }
  return { spec, formatDates };
}

/**
 * A funnel's step values, the comparison window paired by step NAME, and the
 * scale (the largest of either). A scale of zero or less is the funnel's
 * "Nothing to show": it draws no step, so it lists none either.
 */
export function funnelSteps(
  rows: Record<string, any>[],
  key: string | undefined,
  comparisonRows: Record<string, any>[],
  indexKey: string,
): { values: (number | null)[]; comparison: (number | null)[]; scale: number } {
  const values = key == null ? [] : rows.map((r) => measuredNumber(r[key]));
  const comparisonByName = new Map<string, number | null>();
  if (key != null) {
    for (const row of comparisonRows) {
      const name = toStr(row[indexKey]);
      if (!comparisonByName.has(name)) comparisonByName.set(name, measuredNumber(row[key]));
    }
  }
  const comparison = rows.map((r) => {
    const name = toStr(r[indexKey]);
    return comparisonByName.has(name) ? comparisonByName.get(name)! : null;
  });
  const scale = Math.max(0, ...values.map((v) => v ?? 0), ...comparison.map((v) => v ?? 0));
  return { values, comparison, scale };
}
